import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
} from 'react';
import {
    Sgf,
    calcMatAndMarkup,
    Ki,
    Cursor,
    addMoveToCurrentNode,
    zeros,
    empty,
    canMove,
    calcPreventMoveMat,
    calcVisibleArea,
    Effect,
    TNode,
    Theme,
    Markup,
    Analysis,
    AnalysisPointTheme,
    a1ToPos,
    extractBoardSize,
} from 'ghostban';
import {Board, BoardRefType} from './Board';
import {BoardSettings, Scenario} from '@types';
import {
    GHOSTBAN_OPTIONS,
    calcAreaPreventMoveMat,
    useDispatch,
    useSelector,
} from '@utils';
import {boardSlice} from '@reducers';

type ScenarioBoardProps = {
    scenario: Scenario;
    theme?: Theme;
    zoom?: boolean;
    boardSettings?: BoardSettings;
    boardClassName?: string;
    blur?: boolean;
    grayscale?: boolean;
    disabled?: boolean;
    hideCursor?: boolean;
    lastMoveMarkup?: Markup | null;
    size?: number | string;
    style?: React.CSSProperties;
    onAfterMove?: (node: TNode) => void;
    showAnalysis?: boolean;
    showOwnership?: boolean;
    analysis?: Analysis;
    extraMarkup?: string[][];
};

export type ScenarioBoardRefType = {
    reset: () => void;
    takeBack: () => void;
};

export const ScenarioBoard = forwardRef<
    ScenarioBoardRefType,
    ScenarioBoardProps
>(
    (
        {
            scenario,
            theme,
            zoom = false,
            boardSettings,
            boardClassName = 'board',
            disabled = false,
            hideCursor = false,
            lastMoveMarkup,
            size = '100vmin',
            onAfterMove,
            showAnalysis = false,
            showOwnership = false,
            analysis,
            extraMarkup,
        },
        ref
    ) => {
        const boardRef = useRef<BoardRefType>(null);
        const {currentNode, highlights} = useSelector(s => s.board);
        const [mat, setMat] = useState<number[][]>(zeros([19, 19]));
        const [visibleAreaMat, setVisibleAreaMat] = useState<number[][]>(
            zeros([19, 19])
        );
        const [markup, setMarkup] = useState<string[][]>(empty([19, 19]));
        const [effectMat] = useState<string[][]>(empty([19, 19]));
        const [preventMoveMat, setPreventMoveMat] = useState<number[][]>(
            zeros([19, 19])
        );
        const [turn, setTurn] = useState<Ki>(Ki.Empty);
        const [cursor, setCursor] = useState<Cursor>(Cursor.None);

        const dispatch = useDispatch();
        const scenarioId = scenario?.id;
        const scenarioSgf = scenario?.sgf;
        const initialVisibleAreaMat = useMemo(() => {
            if (!scenarioSgf) return zeros([19, 19]);

            const root = new Sgf(scenarioSgf).root;
            if (!root) return zeros([19, 19]);

            return calcMatAndMarkup(root).visibleAreaMat || zeros([19, 19]);
        }, [scenarioSgf]);

        const handleBoardClick = () => {
            const board = boardRef.current?.getBoard();
            if (
                disabled ||
                !currentNode ||
                turn === Ki.Empty ||
                !board ||
                !board.dom ||
                !scenario
            ) {
                return;
            }

            const i = board.cursorPosition[0];
            const j = board.cursorPosition[1];

            const ai = board.actualCursorPosition[0];
            const aj = board.actualCursorPosition[1];
            if (mat[ai][aj] === Ki.Empty && preventMoveMat[ai][aj] === 1) {
                effectMat[ai][aj] = Effect.Ban;
                board.setEffectMat(effectMat);
                board.playEffect();
            }

            if (i < 0 || j < 0) return;
            if (i > 18 || j > 18) return;

            const va = calcVisibleArea(
                initialVisibleAreaMat,
                GHOSTBAN_OPTIONS.extent
            );
            if (i < va[0][0] || i > va[0][1]) {
                return;
            }

            if (j < va[1][0] || j > va[1][1]) {
                return;
            }

            const previousBoardState = currentNode.parent
                ? calcMatAndMarkup(currentNode.parent).mat
                : null;
            if (canMove(mat, i, j, turn, previousBoardState)) {
                const moveAudio = boardRef.current?.getMoveAudio();
                if (!disabled && boardSettings?.moveSound && moveAudio) {
                    void moveAudio.play();
                }

                const node = addMoveToCurrentNode(currentNode, mat, i, j, turn);
                if (node) {
                    dispatch(boardSlice.actions.setCurrentNode(node));
                    if (onAfterMove) {
                        onAfterMove(node);
                    }
                }
            }
        };

        const reset = useCallback(() => {
            if (!scenarioSgf) return;
            const sgf = new Sgf(scenarioSgf);
            const root = sgf.root;
            if (root) {
                dispatch(boardSlice.actions.setCurrentNode(root));
            }
        }, [scenarioSgf, dispatch]);

        const takeBack = useCallback(() => {
            if (currentNode?.parent) {
                dispatch(boardSlice.actions.setCurrentNode(currentNode.parent));
            }
        }, [currentNode, dispatch]);

        useImperativeHandle(ref, () => ({
            reset,
            takeBack,
        }));

        // Initialize board when scenario changes
        useEffect(() => {
            if (!scenarioSgf) return;
            const sgf = new Sgf(scenarioSgf);
            const root = sgf.root;
            if (root) {
                dispatch(boardSlice.actions.setSgf(scenarioSgf));
                dispatch(boardSlice.actions.setCurrentNode(root));
            }
        }, [scenarioId, scenarioSgf, dispatch]);

        useEffect(() => {
            if (!currentNode) return;

            const {
                mat: newMat,
                markup: newMarkup,
                numMarkup,
                visibleAreaMat: newVisibleAreaMat,
            } = calcMatAndMarkup(currentNode);

            setMat(newMat);

            if (extraMarkup) {
                for (let i = 0; i < 19; i++) {
                    for (let j = 0; j < 19; j++) {
                        if (extraMarkup[i]?.[j] && extraMarkup[i][j] !== '') {
                            if (newMarkup[i][j] && newMarkup[i][j] !== '') {
                                newMarkup[i][j] =
                                    `${newMarkup[i][j]}|${extraMarkup[i][j]}`;
                            } else {
                                newMarkup[i][j] = extraMarkup[i][j];
                            }
                        }
                    }
                }
            }

            if (lastMoveMarkup !== undefined) {
                for (let i = 0; i < 19; i++) {
                    for (let j = 0; j < 19; j++) {
                        if (!newMarkup[i]?.[j]) continue;
                        const tokens = newMarkup[i][j].split('|');
                        const nextTokens = tokens
                            .map(token => {
                                if (token !== Markup.Current) return token;
                                return lastMoveMarkup ?? '';
                            })
                            .filter(token => token !== '');
                        newMarkup[i][j] = nextTokens.join('|');
                    }
                }
            }

            if (highlights && highlights.length > 0) {
                const boardSize = extractBoardSize(currentNode);
                for (const highlight of highlights) {
                    if (!highlight) continue;
                    const {x, y} = a1ToPos(highlight);
                    if (x >= 0 && x < boardSize && y >= 0 && y < boardSize) {
                        if (!newMarkup[x][y] || newMarkup[x][y] === '') {
                            newMarkup[x][y] = Markup.Highlight;
                        } else {
                            newMarkup[x][y] += `|${Markup.Highlight}`;
                        }
                    }
                }
            }

            if (boardSettings?.number) {
                for (let i = 0; i < numMarkup.length; i++) {
                    for (let j = 0; j < numMarkup[i].length; j++) {
                        if (numMarkup[i][j] !== '') {
                            newMarkup[i][j] = numMarkup[i][j];
                        }
                    }
                }
            }

            setMarkup(newMarkup);
            setVisibleAreaMat(newVisibleAreaMat || zeros([19, 19]));

            const preventMat = calcPreventMoveMat(currentNode);

            // Merge area-based restrictions from scenario metadata
            const metadata = scenario.metadata;
            if (
                metadata &&
                (metadata.playerAllowedAreas.length > 0 ||
                    metadata.playerDisallowedAreas.length > 0)
            ) {
                const boardSize = extractBoardSize(currentNode);
                const areaMat = calcAreaPreventMoveMat(
                    boardSize,
                    metadata.playerAllowedAreas,
                    metadata.playerDisallowedAreas
                );
                for (let x = 0; x < boardSize; x++) {
                    for (let y = 0; y < boardSize; y++) {
                        if (areaMat[x][y] === 1) {
                            preventMat[x][y] = 1;
                        }
                    }
                }
            }

            setPreventMoveMat(preventMat);

            const lastMove = currentNode.model.moveProps?.[0];
            if (lastMove) {
                const lastToken = lastMove.token;
                if (lastToken === 'W') {
                    setTurn(Ki.Black);
                    setCursor(Cursor.BlackStone);
                } else if (lastToken === 'B') {
                    setTurn(Ki.White);
                    setCursor(Cursor.WhiteStone);
                } else {
                    setTurn(Ki.Empty);
                    setCursor(Cursor.None);
                }
            } else {
                const firstMoveColor =
                    scenario.toMove === 'w' ? Ki.White : Ki.Black;
                setTurn(firstMoveColor);
                setCursor(
                    firstMoveColor === Ki.White
                        ? Cursor.WhiteStone
                        : Cursor.BlackStone
                );
            }
            if (hideCursor) {
                setCursor(Cursor.None);
            }
        }, [
            currentNode,
            scenario.toMove,
            scenario.metadata,
            boardSettings?.number,
            extraMarkup,
            hideCursor,
            lastMoveMarkup,
            highlights,
        ]);

        return (
            <Board
                ref={boardRef}
                boardOptions={{
                    interactive: !disabled,
                    ...boardSettings,
                    theme,
                    zoom,
                    showAnalysis,
                    showOwnership,
                    analysisPointTheme: AnalysisPointTheme.Scenario,
                }}
                boardClassName={boardClassName}
                mat={mat}
                analysis={analysis}
                visibleAreaMat={visibleAreaMat}
                preventMoveMat={preventMoveMat}
                effectMat={effectMat}
                markup={markup}
                cursor={hideCursor ? Cursor.None : cursor}
                turn={turn}
                disabled={disabled}
                size={size}
                onClick={handleBoardClick}
            />
        );
    }
);

ScenarioBoard.displayName = 'ScenarioBoard';
